/**
 * Coalesces "an upload finished" signals into at most one list refresh per
 * interval (+ one immediate refresh when a whole batch settles), so a
 * 500-image upload triggers a handful of list/collection reads - never 500
 * (VistoeraTASK-00522 §26).
 */
export interface RefreshThrottle {
  /** Something changed; refresh within `intervalMs` (trailing). */
  markDirty(): void
  /** Refresh now (if anything is pending) and cancel the pending timer. */
  flushNow(): void
  cancel(): void
}

export function createRefreshThrottle(
  intervalMs: number,
  run: () => void,
  timers: {
    setTimeout: (fn: () => void, ms: number) => unknown
    clearTimeout: (id: unknown) => void
  } = {
    setTimeout: (fn, ms) => setTimeout(fn, ms),
    clearTimeout: (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
  },
): RefreshThrottle {
  let timer: unknown = null
  let dirty = false

  const fire = () => {
    timer = null
    if (!dirty) return
    dirty = false
    run()
  }

  return {
    markDirty() {
      dirty = true
      if (timer === null) timer = timers.setTimeout(fire, intervalMs)
    },
    flushNow() {
      if (timer !== null) {
        timers.clearTimeout(timer)
        timer = null
      }
      fire()
    },
    cancel() {
      if (timer !== null) {
        timers.clearTimeout(timer)
        timer = null
      }
      dirty = false
    },
  }
}
